import { useMemo } from "react"

import _ from "lodash"

import {
    BASE_MAP_ID,
    Layer,
    MapStyles,
    ROAD_ROADSIDE_LAYER,
    ROAD_ROADSIDE_SOURCE,
    useMapStateContext,
    useOrderingLayers,
} from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { CatalogItem, getColorWithOpacity } from "@l2r-front/l2r-utils"

import { getRoadRoadsidePresenceLayerStyle, getRoadRoadsideStateLayerStyle } from "../../utils/getRoadsideConfig"

import { RoadRoadsideInventoriesSource } from "../RoadRoadsideInventoriesSource"

type RoadRoadsideInventoriesLayerProps = {
    road: string,
    type: CatalogItem,
    showSiblingTypes?: boolean,
    filterNonWritableFeatures?: boolean,
    mode?: "presence" | "state",
    mapId?: string,
    mapZoom?: number,
}

export function RoadRoadsideInventoriesLayer(props: RoadRoadsideInventoriesLayerProps) {
    const {
        road,
        type,
        showSiblingTypes = false,
        filterNonWritableFeatures = true,
        mode = "presence",
        mapId = BASE_MAP_ID,
        mapZoom = 0,
    } = props

    const { selectedLinearLocations } = useNetworksStateContext()
    const { getCurrentMapStyle } = useMapStateContext()
    const theme = useTheme()

    const roadRoadsideLayerConfig = useMemo(() => {
        const selectedRoad = selectedLinearLocations?.[0].road
        const currentMapStyle = getCurrentMapStyle(mapId)
        const referentialColor = currentMapStyle === MapStyles.plan ? theme.palette.map.darkReferential
            : theme.palette.map.lightReferential
        const disabledReferentialColor = currentMapStyle === MapStyles.plan ? getColorWithOpacity(theme.palette.map.darkReferential, 0.5)
            : getColorWithOpacity(theme.palette.map.lightReferential, 0.95)

        if (mode === "presence") {
            return getRoadRoadsidePresenceLayerStyle(referentialColor, disabledReferentialColor, theme, type, selectedRoad)
        } else {
            return getRoadRoadsideStateLayerStyle(referentialColor, disabledReferentialColor, theme, selectedRoad)

        }
    }, [getCurrentMapStyle, theme, type, mode, selectedLinearLocations, mapId])

    useOrderingLayers([ROAD_ROADSIDE_LAYER], mapId)

    return (
        <RoadRoadsideInventoriesSource
            id={ROAD_ROADSIDE_SOURCE}
            type="geojson"
            tolerance={.001}
            road={road}
            typeCode={showSiblingTypes ? type.parent : type.code}
            mapId={mapId}
            mapZoom={mapZoom}
            filterNonWritableFeatures={filterNonWritableFeatures}
        >
            <Layer
                id={ROAD_ROADSIDE_LAYER}
                {...roadRoadsideLayerConfig}
            />
        </RoadRoadsideInventoriesSource>
    )
}
